class CustomSearch extends HTMLElement {
  constructor() {
    super();
    this.searchPanel = this.closest("details");
    this.header = document.querySelector("#HeaderContainer");

    this.linkTemplate = this.querySelector("#search-link-template").content;
    this.filterTemplate = this.querySelector("#search-filter-template").content;
    this.filterGroupTemplate = this.querySelector("#search-filter-group-template").content;
    this.productTemplate = this.querySelector("#search-product-template").content;

    this.filterSection = document.querySelector(".js-filters");
    this.productListSection = this.querySelector(".js-search-products");
    this.suggestionSection = this.querySelector(".js-links");

    this.resultsPanel = this.querySelector(".js-results-panel");
    this.loadingState = this.querySelector(".js-search-loading");
    this.loadedState = this.querySelector(".js-search-loaded");
    this.noResultsState = this.querySelector(".js-search-no-results");

    this.input = this.querySelector(".js-search-input");
    this.resetButton = this.querySelector(".js-reset-button");
    this.closeButton = this.querySelector(".js-close-button");
    this.insetScreen = document.querySelector(".js-screen");

    this.searchOpen = false;
    this.firstFocusableElement;
    this.lastFocusableElement;

    this.filters = [];
    this.activeFilters = [];
  }

  connectedCallback() {
    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.addEventListener("input", debounce(() => this.onInput(), 500).bind(this));
    this.input.addEventListener("input", this.setStateOnInput.bind(this));

    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));

    this.resetButton.addEventListener("click", () => {
      this.reset();
      this.input.focus();
    });

    if (this.searchPanel) {
      this.searchPanel.addEventListener("toggle", () => {
        if (!this.searchPanel.open) {
          this.searchOpen = false;
          this.header.classList.remove("searchOpen");
        } else {
          this.input.focus();
          this.searchOpen = true;
          this.firstFocusableElement = this.input;
          this.lastFocusableElement = this.closeButton;
          this.header.classList.add("searchOpen");
        }
      });
    }

    [this.closeButton, this.insetScreen].forEach((closeEl) => {
      if (!closeEl) return;

      closeEl.addEventListener("click", () => this.closeSearch(), true);
    });

    // Keyboard navigation
    document.addEventListener("keydown", (e) => {
      if (!this.searchOpen) return;

      if (e.key === "Tab" && e.shiftKey && document.activeElement === this.firstFocusableElement) {
        e.preventDefault();
        this.lastFocusableElement.focus();
      } else if (e.key === "Tab" && document.activeElement === this.lastFocusableElement) {
        e.preventDefault();
        this.firstFocusableElement.focus();
      } else if (e.key === "Escape") {
        this.closeSearch();
      }
    });
  }

  closeSearch() {
    this.reset();

    if (this.searchPanel) {
      this.searchOpen = false;
      this.searchPanel.focus();
      this.searchPanel.removeAttribute("open");
      this.header.classList.remove("searchOpen");
    }
  }

  // Set the live region state to indicate loading
  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText = this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  // Set the live region text to indicate status
  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  // Set the live region state to indicate loading
  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(this.querySelector("[data-predictive-search-live-region-count-value]").textContent);
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.closeResults(true);
    });
  }

  // Check key controls to maneuver selection of elements
  onKeyup(e) {
    if (!this.getQuery().length) this.closeResults(true);

    e.preventDefault();

    switch (e.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        // Simulate user click
        this.selectOption();
        break;
    }
  }

  onKeydown(e) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (e.code === "ArrowUp" || e.code === "ArrowDown") {
      e.preventDefault();
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
      this.removeAttribute("data-has-results");
    }

    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("loading");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
  }

  // Maneuver selection of elements
  switchOption(direction) {
    // If no results, then return
    if (!this.searchOpen) return;

    // See if up or down arrow key
    const moveUp = direction === "up";

    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll("li, button.predictive-search__item")).filter(
      (el) => el.offsetParent !== null && !el.classList.contains("remove-from-dom")
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  // Simulate user click if element is selected
  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  reset() {
    this.input.value = "";
    this.resetButton.classList.add("tw-hidden");
    this.onInput();
    this.setStateOnInput();
    this.lastFocusableElement = this.closeButton;
  }

  async onInput() {
    const query = this.input.value;
    const activeFilters = this.getActiveFilters();
    await this.updateResultsPanel(query, activeFilters);
  }

  async setStateOnInput() {
    if (!this.input.value) {
      this.resultsPanel.classList.add("tw-hidden");
      this.resetButton.classList.add("tw-hidden");
    } else {
      this.resetButton.classList.remove("tw-hidden");
      this.resultsPanel.classList.remove("tw-hidden");
      this.noResultsState.classList.add("tw-hidden");
      this.loadedState.classList.add("tw-hidden");
      this.loadingState.classList.remove("tw-hidden");
    }
  }

  getActiveFilters() {
    const activeFilterInputs = this.filters.filter((filter) => filter.checked);
    const activeMetafieldFilters = activeFilterInputs
      .filter((input) => input.name.includes("filter.p.m."))
      .map((input) => {
        return {
          productMetafield: {
            key: input.name.split(".").slice(-1)[0],
            namespace: input.name.split(".").slice(-2, -1)[0],
            value: input.value,
          },
        };
      });
    const activeVariantFilters = activeFilterInputs
      .filter((input) => input.name.includes("filter.v.option."))
      .map((input) => {
        return {
          variantOption: {
            name: input.name.split(".").slice(-1)[0],
            value: input.value,
          },
        };
      });

    return [...activeMetafieldFilters, ...activeVariantFilters];
  }

  async updateResultsPanel(query, activeFilters) {
    if (!query) return;
    const queries = await this.fetchPredictiveQueries(query);
    const searchResults = await this.fetchSearchResults(query, activeFilters);

    const resultFilters = searchResults.search.productFilters;
    const productNodes = searchResults.search.edges;

    if (productNodes.length == 0) {
      this.clear();

      // Set the live region state to indicate results
      this.setAttribute("results", true);
      this.setAttribute("data-has-results", true);
      this.setLiveRegionResults();

      this.loadingState.classList.add("tw-hidden");
      this.noResultsState.classList.remove("tw-hidden");
      this.lastFocusableElement = this.querySelector(".js-search-no-results a");
    } else {
      this.clear();
      this.renderPredictiveQueries(queries);
      this.renderFilters(resultFilters);
      this.renderProductNodes(productNodes);
      this.loadingState.classList.add("tw-hidden");
      this.noResultsState.classList.add("tw-hidden");
      this.loadedState.classList.remove("tw-hidden");
    }
  }

  clear() {
    this.filterSection.innerHTML = "";
    this.productListSection.innerHTML = "";
    this.suggestionSection.innerHTML = "";
    this.filters = [];
  }

  async fetchPredictiveQueries(query) {
    const url = window.Shopify.routes.root + `search/suggest.json?q=${query}&resources[type]=query&resources[limit]=5`;
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Network error from REST API response: ${response.status} - ${response.status.text}`);
    }

    const { resources } = await response.json();
    return resources.results.queries;
  }

  renderPredictiveQueries(queries) {
    for (const query of queries) {
      const { url, text } = query;
      const el = this.linkTemplate.cloneNode(true);
      const anchor = el.querySelector("a");
      anchor.href = url;
      anchor.ariaLabel = text;
      anchor.textContent = text;
      this.suggestionSection.appendChild(el);
    }
  }

  async fetchSearchResults(query, filters) {
    // Set live region state to indicate loading
    this.setLiveRegionLoadingState();

    const response = await fetch(window.Shopify.routes.root + "api/2024-04/graphql.json", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": "b40447c1617bfffa0068de7ad3c1eaeb",
      },
      body: JSON.stringify({
        query: `
          query searchProducts($query: String!, $first: Int, $productFilters: [ProductFilter!]) {
            search(
              query: $query
              first: $first
              productFilters: $productFilters
              types: PRODUCT
            ) {
              edges {
                node {
                  ... on Product {
                    title
                    tags
                    onlineStoreUrl
                    featuredImage {
                      url(transform: {maxWidth: 300})
                    }
                    vendor
                    priceRange {
                      minVariantPrice {
                        amount
                      }
                    }
                    id
                    variants(first: 250) {
                      edges {
                        node {
                          sku
                        }
                      }
                    }
                  }
                }
              }
              productFilters {
                values {
                  count
                  label
                  id
                }
                label
                id
              }
            }
          }`,
        variables: {
          query: query,
          first: 20,
          productFilters: filters,
        },
        operationName: "",
      }),
    });

    if (!response.ok) {
      throw new Error(`Network error from GraphQL response: ${response.status} - ${response.status.text}`);
    }

    const { data, errors } = await response.json();

    if (errors) {
      const message = errors.map((error) => error.message).join("\n");
      throw new Error(`GraphQL errors:\n${message}`);
    }

    return data;
  }

  renderFilters(filters) {
    for (const filter of filters) {
      let count = 0;
      let open = false;
      const groupEl = this.filterGroupTemplate.cloneNode(true);
      const summary = groupEl.querySelector(".js-summary");
      const content = groupEl.querySelector(".js-content");

      summary.textContent = filter.label;
      for (const value of filter.values) {
        if (value.count == 0) continue;
        const el = this.filterTemplate.cloneNode(true);
        const input = el.querySelector("input");
        const text = el.querySelector(".js-filter-text");

        if (this.activeFilters.includes(value.id)) {
          input.checked = true;
          open = true;
        }
        text.textContent = value.label;
        input.name = filter.id;
        input.value = value.label;
        input.addEventListener("change", this.onInput.bind(this));
        input.addEventListener("change", this.setStateOnInput.bind(this));
        input.addEventListener("change", (e) => this.onFilterChange(e, value));

        content.appendChild(el);
        this.filters.push(input);
        count++;
      }
      if (open) groupEl.querySelector("details").setAttribute("open", "");
      if (count > 0) {
        this.filterSection.appendChild(groupEl);
      }
    }
  }

  onFilterChange(e, value) {
    if (e.target.checked) {
      this.activeFilters.push(value.id);
    } else {
      const ind = this.activeFilters.indexOf(value.id);
      this.activeFilters.splice(ind, 1);
    }
    this.onInput();
    this.setStateOnInput();
  }

  renderProductNodes(productNodes) {
    for (const { node } of productNodes) {
      const title = node.title;
      const imageUrl = node.featuredImage.url;
      const sku = node.variants.edges[0].node.sku;
      const productUrl =
        node.onlineStoreUrl ||
        "/products/" +
          node.title
            .toLowerCase()
            .replace(/[^\w\u00C0-\u024f]+/g, "-")
            .replace(/^-+|-+$/g, "");

      const el = this.productTemplate.cloneNode(true);

      el.querySelector(".js-product-link").href = productUrl;
      el.querySelector(".js-product-title").textContent = title;
      el.querySelector(".js-image").src = imageUrl;
      el.querySelector(".js-product-sku").textContent = sku;
      el.querySelector(".js-product-details").href = productUrl;

      const productAwardTag = node.tags?.find((tag) => tag.includes("udsubtitle_"));
      if (productAwardTag) {
        el.querySelector(".js-product-subtitle").classList.replace("tw-hidden", "tw-flex");
        el.querySelector(".js-product-subtitle p").textContent = productAwardTag.replace("udsubtitle_", "");
      }

      this.productListSection.appendChild(el);
    }

    this.lastFocusableElement = this.productListSection.lastElementChild.querySelector(".ud-btn");
  }
}

if (!customElements.get("custom-search")) customElements.define("custom-search", CustomSearch);
